// components/LogoCarousel.ts : bandeaux défilants (marquee) des plateformes et blockchains supportées
//
// Trois bandeaux en boucle infinie, alternant le sens de défilement :
//   1. Exchanges et services  : de droite à gauche
//   2. Blockchains EVM        : de gauche à droite
//   3. Autres blockchains     : de droite à gauche
// La liste reflète les plateformes ACTIVES dans l'application (supported_platforms.json de l'API) ;
// les exchanges fermés (Bittrex, Multis...) et les chaînes désactivées (Bittensor) n'y figurent pas.

interface Logo {
  src: string;
  alt: string;
  href?: string;
}

interface MarqueeRow {
  id: string;
  /** 'rtl' : défile de droite à gauche (sens par défaut), 'ltr' : de gauche à droite. */
  direction: 'rtl' | 'ltr';
  logos: Logo[];
}

/** Durée d'un tour complet, proportionnelle au nombre de logos pour une vitesse homogène entre les bandeaux. */
const SECONDS_PER_LOGO = 3.5;

const EXCHANGES_AND_SERVICES: Logo[] = [
  { src: "assets/images/platforms/binance_logo.webp", alt: "Binance", href: "https://www.binance.com" },
  { src: "assets/images/platforms/Coinbase.svg.webp", alt: "Coinbase", href: "https://www.coinbase.com" },
  { src: "assets/images/platforms/Kraken-Emblem.webp", alt: "Kraken", href: "https://www.kraken.com" },
  { src: "assets/images/platforms/coinhouse-logo.webp", alt: "Coinhouse", href: "https://www.coinhouse.com" },
  { src: "assets/images/platforms/paymium.webp", alt: "Paymium", href: "https://www.paymium.com" },
  { src: "assets/images/platforms/Meria.webp", alt: "Meria", href: "https://www.meria.com" },
  { src: "assets/images/platforms/bitpanda-logo.webp", alt: "Bitpanda", href: "https://www.bitpanda.com" },
  { src: "assets/images/platforms/bitstamp-logo.webp", alt: "Bitstamp", href: "https://www.bitstamp.net" },
  { src: "assets/images/platforms/bitvavo-logo.webp", alt: "Bitvavo", href: "https://www.bitvavo.com" },
  { src: "assets/images/platforms/Bitfinex.webp", alt: "Bitfinex", href: "https://www.bitfinex.com" },
  { src: "assets/images/platforms/bitmex-logo.webp", alt: "BitMEX", href: "https://www.bitmex.com" },
  { src: "assets/images/platforms/crypto-com.svg", alt: "Crypto.com", href: "https://crypto.com" },
  { src: "assets/images/platforms/gate.io-logo.webp", alt: "Gate.io", href: "https://www.gate.io" },
  { src: "assets/images/platforms/gemini-logo.webp", alt: "Gemini", href: "https://www.gemini.com" },
  { src: "assets/images/platforms/kucoin-logo.webp", alt: "KuCoin", href: "https://www.kucoin.com" },
  { src: "assets/images/platforms/Luno_logo.webp", alt: "Luno", href: "https://www.luno.com" },
  { src: "assets/images/platforms/OKX_logo.svg", alt: "OKX", href: "https://www.okx.com" },
  { src: "assets/images/platforms/nexo-logo.webp", alt: "Nexo", href: "https://nexo.com" },
  { src: "assets/images/platforms/revolut-logo.webp", alt: "Revolut", href: "https://www.revolut.com" },
  { src: "assets/images/platforms/trade-republic-logo.webp", alt: "Trade Republic", href: "https://traderepublic.com" },
  { src: "assets/images/platforms/ledger-logo.webp", alt: "Ledger", href: "https://www.ledger.com" },
  { src: "assets/images/platforms/finary-logo.webp", alt: "Finary", href: "https://finary.com" },
  { src: "assets/images/platforms/deskoin-logo.webp", alt: "Deskoin", href: "https://deskoin.com" },
];

const EVM_CHAINS: Logo[] = [
  { src: "assets/images/platforms/ETH_logo.webp", alt: "Ethereum", href: "https://ethereum.org" },
  { src: "assets/images/platforms/Arbitrum_Vertical.webp", alt: "Arbitrum", href: "https://arbitrum.io" },
  { src: "assets/images/platforms/base-logo.webp", alt: "Base", href: "https://www.base.org" },
  { src: "assets/images/platforms/bnb-chain.webp", alt: "BNB Smart Chain", href: "https://www.bnbchain.org" },
  { src: "assets/images/platforms/polygon-logo.webp", alt: "Polygon", href: "https://polygon.technology" },
  { src: "assets/images/platforms/optimism-logo.webp", alt: "Optimism", href: "https://www.optimism.io" },
  { src: "assets/images/platforms/Avalanche.webp", alt: "Avalanche", href: "https://www.avax.network" },
  { src: "assets/images/platforms/zkSync.webp", alt: "zkSync", href: "https://zksync.io" },
  { src: "assets/images/platforms/cronos-logo.webp", alt: "Cronos", href: "https://cronos.org" },
  { src: "assets/images/platforms/linea-logo.webp", alt: "Linea", href: "https://linea.build" },
  { src: "assets/images/platforms/gnosis-logo.webp", alt: "Gnosis", href: "https://www.gnosis.io" },
  { src: "assets/images/platforms/sonic-logo.webp", alt: "Sonic", href: "https://www.soniclabs.com" },
  { src: "assets/images/platforms/conflux-logo.webp", alt: "Conflux", href: "https://confluxnetwork.org" },
  { src: "assets/images/platforms/sei-logo.webp", alt: "Sei", href: "https://www.sei.io" },
  { src: "assets/images/platforms/kaia-logo.webp", alt: "Kaia", href: "https://www.kaia.io" },
  { src: "assets/images/platforms/Scroll-logo.webp", alt: "Scroll", href: "https://scroll.io" },
  { src: "assets/images/platforms/Blast-Logo.webp", alt: "Blast", href: "https://blast.io" },
  { src: "assets/images/platforms/mantle-logo.webp", alt: "Mantle", href: "https://www.mantle.xyz" },
  { src: "assets/images/platforms/mode-logo.webp", alt: "Mode", href: "https://www.mode.network" },
  { src: "assets/images/platforms/celo-logo.webp", alt: "Celo", href: "https://celo.org" },
  { src: "assets/images/platforms/metis-logo.webp", alt: "Metis", href: "https://www.metis.io" },
  { src: "assets/images/platforms/frax-logo.webp", alt: "Fraxtal", href: "https://frax.finance" },
  { src: "assets/images/platforms/Moonbeam-Logo.webp", alt: "Moonbeam", href: "https://moonbeam.network" },
];

const OTHER_CHAINS: Logo[] = [
  { src: "assets/images/platforms/bitcoin-logo.webp", alt: "Bitcoin", href: "https://bitcoin.org" },
  { src: "assets/images/platforms/litecoin-logo.webp", alt: "Litecoin", href: "https://litecoin.org" },
  { src: "assets/images/platforms/dogecoin-logo.webp", alt: "Dogecoin", href: "https://dogecoin.com" },
  { src: "assets/images/platforms/xrp-logo.webp", alt: "XRP", href: "https://xrpl.org" },
  { src: "assets/images/platforms/stellar-logo.webp", alt: "Stellar", href: "https://stellar.org" },
  { src: "assets/images/platforms/Solana-Logo.webp", alt: "Solana", href: "https://solana.com" },
  { src: "assets/images/platforms/Cardano-logo.webp", alt: "Cardano", href: "https://cardano.org" },
  { src: "assets/images/platforms/tron-logo.webp", alt: "TRON", href: "https://tron.network" },
  { src: "assets/images/platforms/tezos-logo.svg", alt: "Tezos", href: "https://tezos.com" },
  { src: "assets/images/platforms/toncoin-logo.webp", alt: "TON", href: "https://ton.org" },
  { src: "assets/images/platforms/full-near-logo.webp", alt: "NEAR", href: "https://near.org" },
  { src: "assets/images/platforms/Aptos_logo.webp", alt: "Aptos", href: "https://aptoslabs.com" },
  { src: "assets/images/platforms/Sui-logo.webp", alt: "Sui", href: "https://sui.io" },
  { src: "assets/images/platforms/Polkadot-Logo.webp", alt: "Polkadot", href: "https://polkadot.network" },
  { src: "assets/images/platforms/multiversx-logo.webp", alt: "MultiversX", href: "https://multiversx.com" },
  { src: "assets/images/platforms/vechain-logo.webp", alt: "VeChain", href: "https://www.vechain.org" },
  { src: "assets/images/platforms/hyperliquid-logo.webp", alt: "Hyperliquid", href: "https://hyperliquid.xyz" },
  { src: "assets/images/platforms/polymarket-logo.webp", alt: "Polymarket", href: "https://polymarket.com" },
  { src: "assets/images/platforms/Cosmos-Logo.webp", alt: "Cosmos", href: "https://cosmos.network" },
  { src: "assets/images/platforms/osmosis-logo.webp", alt: "Osmosis", href: "https://osmosis.zone" },
  { src: "assets/images/platforms/celestia-logo.webp", alt: "Celestia", href: "https://celestia.org" },
  { src: "assets/images/platforms/injective-logo.webp", alt: "Injective", href: "https://injective.com" },
  { src: "assets/images/platforms/akash-logo.webp", alt: "Akash", href: "https://akash.network" },
  { src: "assets/images/platforms/kujira-logo.webp", alt: "Kujira", href: "https://kujira.network" },
  { src: "assets/images/platforms/neutron-logo.webp", alt: "Neutron", href: "https://www.neutron.org" },
  { src: "assets/images/platforms/dymension-logo.webp", alt: "Dymension", href: "https://dymension.xyz" },
  { src: "assets/images/platforms/Secret-network-logo.webp", alt: "Secret Network", href: "https://scrt.network" },
  { src: "assets/images/platforms/juno-logo.webp", alt: "Juno", href: "https://junonetwork.io" },
];

const ROWS: MarqueeRow[] = [
  { id: 'exchanges', direction: 'rtl', logos: EXCHANGES_AND_SERVICES },
  { id: 'evm', direction: 'ltr', logos: EVM_CHAINS },
  { id: 'chains', direction: 'rtl', logos: OTHER_CHAINS },
];

export const createLogoCarousel = () => {
  const renderLogos = (logos: Logo[]) => {
    return logos.map(logo => {
      const item = document.createElement('div');
      item.className = 'logo-marquee__item';

      const img = document.createElement('img');
      img.src = logo.src;
      img.alt = logo.alt;
      img.width = 180;
      img.height = 80;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.className = 'logo-marquee__img';

      if (logo.href) {
        const link = document.createElement('a');
        link.href = logo.href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.appendChild(img);
        item.appendChild(link);
      } else {
        item.appendChild(img);
      }

      return item;
    });
  };

  /** Un jeu de logos = un bloc ; la piste en contient deux copies pour une boucle sans raccord. */
  const renderSet = (logos: Logo[]) => {
    const set = document.createElement('div');
    set.className = 'logo-marquee__set';
    renderLogos(logos).forEach(item => set.appendChild(item));
    return set;
  };

  const createRow = (row: MarqueeRow) => {
    const marquee = document.createElement('div');
    marquee.className = `logo-marquee logo-marquee--${row.direction}`;
    marquee.dataset.marquee = row.id;
    marquee.style.setProperty('--marquee-duration', `${Math.round(row.logos.length * SECONDS_PER_LOGO)}s`);

    const track = document.createElement('div');
    track.className = 'logo-marquee__track';
    track.appendChild(renderSet(row.logos));
    track.appendChild(renderSet(row.logos));
    marquee.appendChild(track);

    // Pause au survol, bandeau par bandeau
    marquee.addEventListener('mouseenter', () => {
      track.style.animationPlayState = 'paused';
    });
    marquee.addEventListener('mouseleave', () => {
      track.style.animationPlayState = 'running';
    });

    return marquee;
  };

  const createMarquee = () => {
    const wrapper = document.createElement('div');
    wrapper.className = 'logo-marquee-wrapper';

    // Titre
    const titleContainer = document.createElement('div');
    titleContainer.className = 'text-center mb-12';
    const title = document.createElement('h2');
    title.className = 'gradient-text font-bold text-primary-900';
    title.setAttribute('data-translate', 'logos.title');
    title.textContent = 'Nos plateformes et blockchains supportées';
    titleContainer.appendChild(title);
    wrapper.appendChild(titleContainer);

    // Les trois bandeaux
    const rows = document.createElement('div');
    rows.className = 'logo-marquee-rows';
    ROWS.forEach(row => rows.appendChild(createRow(row)));
    wrapper.appendChild(rows);

    return wrapper;
  };

  const logoSection = document.querySelector('[data-section="logo-carousel"]');
  if (logoSection) {
    logoSection.innerHTML = '';
    logoSection.appendChild(createMarquee());
  }
};
